import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Form, Button, Spinner, Modal } from 'react-bootstrap';
import { BsPencil, BsTrash, BsThreeDotsVertical } from 'react-icons/bs';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import StarDivider from '../components/uiComponents/StarDivider';
import Toast from '../components/Toast';
import Pagination from '../components/Pagination';
import prayerCenterService from '../api/prayerCenterService';
import representationService from '../api/representationService';

const TYPE_OPTIONS = [
  { value: 'espace_faustine', label: 'Espace Sainte Faustine' },
  { value: 'atmosphere_victoire', label: 'Atmosphère de la Victoire' },
  { value: 'centre_priere', label: 'Centre de prière' },
  { value: 'eglise', label: 'Église' },
  { value: 'chapelle', label: 'Chapelle' },
  { value: 'salle', label: 'Salle de culte' },
  { value: 'autre', label: 'Autre' },
];

const STATUT_OPTIONS = [
  { value: 'active', label: 'Actif' },
  { value: 'inactive', label: 'Inactif' },
];

const EMPTY_FORM = {
  representation_id: '',
  nom: '',
  type_lieu: 'espace_faustine',
  lieu: '',
  map_link: '',
  telephone_1: '',
  telephone_2: '',
  statut: 'active',
};

function extractError(err) {
  if (err?.response?.data?.errors?.length)
    return err.response.data.errors.map((e) => e.message).join(', ');
  return err?.response?.data?.message || err?.message || 'Une erreur est survenue';
}

const typeLabel = (v) => TYPE_OPTIONS.find((t) => t.value === v)?.label || v;
const statutStyle = (v) => ({
  padding: '2px 10px',
  borderRadius: 10,
  fontSize: 11,
  backgroundColor: v === 'active' ? '#d4edda' : '#f8d7da',
  color: v === 'active' ? '#155724' : '#721c24',
});

export default function PrayerCentersPage() {
  const userRole = useSelector((s) => s.auth.user?.role);
  const isViewer = userRole === 'viewer';

  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1, currentPage: 1 });
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState('list'); // 'list' | 'add' | 'edit'
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [toast, setToast] = useState({ message: '', type: 'success' });

  const [filters, setFilters] = useState({
    representation_id: '',
    statut: '',
    nom: '',
    page: 1,
    limit: 10,
  });

  const showToast = (message, type = 'success') => setToast({ message, type });
  const hideToast = () => setToast({ message: '', type: 'success' });

  // Load country list for dropdown
  useEffect(() => {
    representationService.listCountries()
      .then(({ data: res }) => setCountries(res.data || []))
      .catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.representation_id) params.representation_id = filters.representation_id;
      if (filters.statut) params.statut = filters.statut;
      if (filters.nom) params.nom = filters.nom;
      params.page = filters.page;
      params.limit = filters.limit;

      const { data: res } = await prayerCenterService.listAll(params);
      setData(res.data || []);
      setPagination(res.pagination || { total: 0, totalPages: 1, currentPage: 1 });
    } catch (err) {
      showToast(extractError(err), 'danger');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleFilterChange = (e) =>
    setFilters((f) => ({ ...f, [e.target.name]: e.target.value, page: 1 }));

  const handleClearFilters = () =>
    setFilters({ representation_id: '', statut: '', nom: '', page: 1, limit: 10 });

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    setFormErrors((fe) => ({ ...fe, [name]: '' }));
  };

  const validateForm = () => {
    const errors = {};
    if (!form.representation_id) errors.representation_id = 'Pays / Représentation requis';
    if (!form.nom.trim()) errors.nom = 'Nom requis';
    return errors;
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditItem(null);
    setFormErrors({});
  };

  const handleSave = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length) { setFormErrors(errors); return; }

    setSaving(true);
    try {
      const payload = {
        representation_id: form.representation_id,
        nom: form.nom,
        type_lieu: form.type_lieu,
        lieu: form.lieu || null,
        map_link: form.map_link || null,
        telephone_1: form.telephone_1 || null,
        telephone_2: form.telephone_2 || null,
        statut: form.statut,
      };

      if (view === 'add') {
        await prayerCenterService.create(payload);
        showToast('Centre de prière ajouté avec succès');
      } else {
        // representation_id not needed for update
        const { representation_id, ...updatePayload } = payload;
        await prayerCenterService.update(editItem.id, updatePayload);
        showToast('Centre de prière mis à jour avec succès');
      }
      setView('list');
      resetForm();
      fetchData();
    } catch (err) {
      showToast(extractError(err), 'danger');
    } finally {
      setSaving(false);
    }
  };

  const handleEditOpen = (row) => {
    setForm({
      representation_id: row.representation_id,
      nom: row.nom || '',
      type_lieu: row.type_lieu || 'centre_priere',
      lieu: row.lieu || '',
      map_link: row.map_link || '',
      telephone_1: row.telephone_1 || '',
      telephone_2: row.telephone_2 || '',
      statut: row.statut || 'active',
    });
    setEditItem(row);
    setFormErrors({});
    setView('edit');
  };

  const handleDelete = async () => {
    try {
      await prayerCenterService.remove(deleteTarget.id);
      showToast('Centre de prière supprimé avec succès');
      setDeleteTarget(null);
      fetchData();
    } catch (err) {
      showToast(extractError(err), 'danger');
      setDeleteTarget(null);
    }
  };

  const countryName = (id) =>
    countries.find((c) => c.id === id)?.pays || '—';

  const countryFlag = (id) =>
    countries.find((c) => c.id === id)?.drapeau_url || null;

  const hasFilters = filters.representation_id || filters.statut || filters.nom;

  return (
    <div>
      <Toast message={toast.message} type={toast.type} onClose={hideToast} />

      <div className="text-center mb-3">
        <h5 style={{ fontFamily: 'serif', letterSpacing: 3, fontWeight: 400 }}>
          CENTRES DE PRIÈRE
        </h5>
        <StarDivider lineWidth="5%" />
      </div>

      {/* Filters */}
      <div className="bg-white p-3 rounded mb-4" style={{ border: '1px solid #eee' }}>
        <div
          className="d-md-none d-flex justify-content-between align-items-center"
          style={{ cursor: 'pointer', marginBottom: filtersOpen ? 12 : 0 }}
          onClick={() => setFiltersOpen((v) => !v)}
        >
          <span className="fw-semibold" style={{ fontSize: 14 }}>Filtres de recherche</span>
          <span>{filtersOpen ? <FaChevronUp size={18} /> : <FaChevronDown size={18} />}</span>
        </div>
        <div className={filtersOpen ? '' : 'd-none d-md-block'}>
          <Row className="gy-1 gx-3 align-items-end">
            <Col md={3} xs={12}>
              <Form.Label className="small mb-0 fw-semibold">Pays / Représentation</Form.Label>
              <Form.Select
                name="representation_id"
                value={filters.representation_id}
                onChange={handleFilterChange}
                size="sm"
              >
                <option value="">Tous les pays</option>
                {countries.map((c) => (
                  <option key={c.id} value={c.id}>{c.pays}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3} xs={12}>
              <Form.Label className="small mb-0 fw-semibold">Nom</Form.Label>
              <Form.Control
                size="sm"
                name="nom"
                value={filters.nom}
                onChange={handleFilterChange}
                placeholder="Rechercher par nom..."
              />
            </Col>
            <Col md={3} xs={12}>
              <Form.Label className="small mb-0 fw-semibold">Statut</Form.Label>
              <Form.Select name="statut" value={filters.statut} onChange={handleFilterChange} size="sm">
                <option value="">Tous les statuts</option>
                {STATUT_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3} xs={12} className="d-flex gap-2 justify-content-md-end mt-2 mt-md-0">
              {hasFilters && (
                <Button onClick={handleClearFilters} variant="outline-secondary" size="sm" className="text-uppercase px-2">
                  EFFACER
                </Button>
              )}
            </Col>
          </Row>
        </div>
      </div>

      {/* Add / Edit form */}
      {!isViewer && (view === 'add' || view === 'edit') && (
        <div style={{ maxWidth: 640, margin: '0 auto 24px' }}>
          <h6 className="fw-bold mb-2">
            {view === 'add' ? 'Ajouter un centre de prière' : 'Modifier le centre de prière'}
          </h6>
          <Form className="bg-white rounded p-3" style={{ border: '1px solid #eee' }}>

            {/* Pays / Représentation */}
            <Form.Group as={Row} className="mb-3 align-items-center">
              <Form.Label column sm={4} className="fw-semibold" style={{ fontSize: 14 }}>
                Pays / Représentation :
              </Form.Label>
              <Col sm={8}>
                <Form.Select
                  name="representation_id"
                  value={form.representation_id}
                  onChange={handleFormChange}
                  isInvalid={!!formErrors.representation_id}
                  disabled={view === 'edit'}
                >
                  <option value="">— Sélectionner un pays —</option>
                  {countries.map((c) => (
                    <option key={c.id} value={c.id}>{c.pays}</option>
                  ))}
                </Form.Select>
                {formErrors.representation_id && (
                  <div className="invalid-feedback d-block">{formErrors.representation_id}</div>
                )}
              </Col>
            </Form.Group>

            {/* Nom */}
            <Form.Group as={Row} className="mb-3 align-items-center">
              <Form.Label column sm={4} className="fw-semibold" style={{ fontSize: 14 }}>
                Nom du centre :
              </Form.Label>
              <Col sm={8}>
                <Form.Control
                  name="nom"
                  value={form.nom}
                  onChange={handleFormChange}
                  placeholder="Ex: Centre de Prière MCM Paris Nord"
                  isInvalid={!!formErrors.nom}
                />
                {formErrors.nom && (
                  <div className="invalid-feedback d-block">{formErrors.nom}</div>
                )}
              </Col>
            </Form.Group>

            {/* Type de lieu */}
            <Form.Group as={Row} className="mb-3 align-items-center">
              <Form.Label column sm={4} className="fw-semibold" style={{ fontSize: 14 }}>
                Type de lieu :
              </Form.Label>
              <Col sm={8}>
                <Form.Select name="type_lieu" value={form.type_lieu} onChange={handleFormChange}>
                  {TYPE_OPTIONS.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </Form.Select>
              </Col>
            </Form.Group>

            {/* Lieu / Adresse */}
            <Form.Group as={Row} className="mb-3 align-items-center">
              <Form.Label column sm={4} className="fw-semibold" style={{ fontSize: 14 }}>
                Adresse / Lieu :
              </Form.Label>
              <Col sm={8}>
                <Form.Control
                  name="lieu"
                  value={form.lieu}
                  onChange={handleFormChange}
                  placeholder="Ex: 12 Rue de la Paix, Paris"
                />
              </Col>
            </Form.Group>

            {/* Lien Google Maps */}
            <Form.Group as={Row} className="mb-3 align-items-center">
              <Form.Label column sm={4} className="fw-semibold" style={{ fontSize: 14 }}>
                Lien Google Maps :
              </Form.Label>
              <Col sm={8}>
                <Form.Control
                  name="map_link"
                  value={form.map_link}
                  onChange={handleFormChange}
                  placeholder="https://maps.google.com/..."
                />
              </Col>
            </Form.Group>

            {/* Téléphone 1 */}
            <Form.Group as={Row} className="mb-3 align-items-center">
              <Form.Label column sm={4} className="fw-semibold" style={{ fontSize: 14 }}>
                Téléphone 1 :
              </Form.Label>
              <Col sm={8}>
                <Form.Control
                  name="telephone_1"
                  value={form.telephone_1}
                  onChange={handleFormChange}
                  placeholder="+33 6 12 34 56 78"
                />
              </Col>
            </Form.Group>

            {/* Téléphone 2 */}
            <Form.Group as={Row} className="mb-3 align-items-center">
              <Form.Label column sm={4} className="fw-semibold" style={{ fontSize: 14 }}>
                Téléphone 2 :
              </Form.Label>
              <Col sm={8}>
                <Form.Control
                  name="telephone_2"
                  value={form.telephone_2}
                  onChange={handleFormChange}
                  placeholder="+33 6 98 76 54 32 (optionnel)"
                />
              </Col>
            </Form.Group>

            {/* Statut */}
            <Form.Group as={Row} className="mb-3 align-items-center">
              <Form.Label column sm={4} className="fw-semibold" style={{ fontSize: 14 }}>
                Statut :
              </Form.Label>
              <Col sm={8}>
                <Form.Select name="statut" value={form.statut} onChange={handleFormChange}>
                  {STATUT_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </Form.Select>
              </Col>
            </Form.Group>

            <div className="text-center mt-4 d-flex gap-3 justify-content-center">
              <Button
                size="sm"
                variant="outline-secondary"
                type="button"
                onClick={() => { setView('list'); resetForm(); }}
                style={{ letterSpacing: 1, paddingInline: 24 }}
              >
                ANNULER
              </Button>
              <Button
                size="sm"
                type="button"
                disabled={saving}
                onClick={handleSave}
                style={{ backgroundColor: '#8b1512', border: 'none', letterSpacing: 1, paddingInline: 40 }}
              >
                {saving
                  ? <Spinner size="sm" animation="border" />
                  : view === 'add' ? 'ENREGISTRER' : 'ENREGISTRER LES MODIFICATIONS'}
              </Button>
            </div>
          </Form>
        </div>
      )}

      {/* List */}
      {view === 'list' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
            <h6 style={{ fontWeight: 700, margin: 0 }}>Liste des centres de prière</h6>
            {!isViewer && (
              <Button
                size="sm"
                style={{ backgroundColor: '#8b1512', border: 'none' }}
                onClick={() => { resetForm(); setView('add'); }}
              >
                + Nouveau Centre
              </Button>
            )}
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" style={{ color: '#8b1512' }} />
            </div>
          ) : data.length === 0 ? (
            <div className="text-center py-5 text-secondary">Aucun centre de prière trouvé.</div>
          ) : (
            <>
              {/* Desktop table */}
              <div style={{ overflowX: 'auto' }} className="d-none d-md-block table-mcm-wrap">
                <table className="table-mcm">
                  <thead>
                    <tr>
                      {['Pays', 'Nom', 'Type', 'Adresse', 'Téléphone 1', 'Téléphone 2', 'Statut', 'Actions'].map((h) => (
                        <th key={h} className="th-mcm">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((row) => (
                      <tr key={row.id}>
                        <td className="td-mcm">
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            {countryFlag(row.representation_id) && (
                              <img
                                src={countryFlag(row.representation_id)}
                                alt=""
                                style={{ width: 22, height: 16, objectFit: 'cover', borderRadius: 2, flexShrink: 0 }}
                              />
                            )}
                            <span>{row.representation?.pays || countryName(row.representation_id)}</span>
                          </div>
                        </td>
                        <td className="td-mcm" style={{ fontWeight: 500 }}>{row.nom}</td>
                        <td className="td-mcm">{typeLabel(row.type_lieu)}</td>
                        <td className="td-mcm">
                          {row.lieu ? (
                            row.map_link ? (
                              <a href={row.map_link} target="_blank" rel="noopener noreferrer" style={{ color: '#8b1512', fontSize: 13 }}>
                                {row.lieu}
                              </a>
                            ) : (
                              <span style={{ fontSize: 13 }}>{row.lieu}</span>
                            )
                          ) : '—'}
                        </td>
                        <td className="td-mcm">{row.telephone_1 || '—'}</td>
                        <td className="td-mcm">{row.telephone_2 || '—'}</td>
                        <td className="td-mcm">
                          <span style={statutStyle(row.statut)}>
                            {row.statut === 'active' ? 'Actif' : 'Inactif'}
                          </span>
                        </td>
                        <td className="td-mcm" style={{ whiteSpace: 'nowrap' }}>
                          {!isViewer && (
                            <>
                              <button className="icon-btn-mcm" onClick={() => handleEditOpen(row)}>
                                <BsPencil size={14} color="#555" />
                              </button>
                              <button className="icon-btn-mcm" onClick={() => setDeleteTarget(row)}>
                                <BsTrash size={14} color="#8b1512" />
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="d-md-none">
                <div className="d-flex flex-column gap-3">
                  {data.map((row) => (
                    <div key={row.id} className="bg-white rounded p-3" style={{ border: '1px solid #eee' }}>
                      <div className="d-flex justify-content-between align-items-start border-bottom pb-2 mb-2">
                        <div>
                          <div style={{ fontWeight: 600, color: '#8b1512', display: 'flex', alignItems: 'center', gap: 6 }}>
                            {countryFlag(row.representation_id) && (
                              <img
                                src={countryFlag(row.representation_id)}
                                alt=""
                                style={{ width: 20, height: 14, objectFit: 'cover', borderRadius: 2 }}
                              />
                            )}
                            {row.representation?.pays || countryName(row.representation_id)}
                          </div>
                          <div style={{ fontSize: 13, fontWeight: 500, marginTop: 2 }}>{row.nom}</div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={statutStyle(row.statut)}>
                            {row.statut === 'active' ? 'Actif' : 'Inactif'}
                          </span>
                          {!isViewer && (
                            <div style={{ position: 'relative' }}>
                              <BsThreeDotsVertical
                                size={18}
                                color="#555"
                                style={{ cursor: 'pointer' }}
                                onClick={() => setOpenMenuId(openMenuId === row.id ? null : row.id)}
                              />
                              {openMenuId === row.id && (
                                <div style={{
                                  position: 'absolute', right: 0, top: '100%', zIndex: 100,
                                  backgroundColor: '#fff', border: '1px solid #e0e0e0',
                                  borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                                  minWidth: 130, overflow: 'hidden', marginTop: 4,
                                }}>
                                  <button
                                    type="button"
                                    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 14px', background: 'none', border: 'none', fontSize: 13, cursor: 'pointer' }}
                                    onClick={() => { handleEditOpen(row); setOpenMenuId(null); }}
                                  >
                                    <BsPencil size={13} color="#555" /> Modifier
                                  </button>
                                  <button
                                    type="button"
                                    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 14px', background: 'none', border: 'none', fontSize: 13, color: '#8b1512', cursor: 'pointer' }}
                                    onClick={() => { setDeleteTarget(row); setOpenMenuId(null); }}
                                  >
                                    <BsTrash size={13} color="#8b1512" /> Supprimer
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div style={{ fontSize: 13 }}>
                        <div><span className="fw-semibold">Type : </span>{typeLabel(row.type_lieu)}</div>
                        {row.lieu && <div><span className="fw-semibold">Adresse : </span>{row.lieu}</div>}
                        {row.telephone_1 && <div><span className="fw-semibold">Tél 1 : </span>{row.telephone_1}</div>}
                        {row.telephone_2 && <div><span className="fw-semibold">Tél 2 : </span>{row.telephone_2}</div>}
                        {row.map_link && (
                          <div>
                            <a href={row.map_link} target="_blank" rel="noopener noreferrer" style={{ color: '#8b1512', fontSize: 13 }}>
                              Voir sur la carte
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          <Pagination
            currentPage={filters.page}
            totalPages={pagination.totalPages}
            onPageChange={(page) => setFilters((f) => ({ ...f, page }))}
          />
        </>
      )}

      {/* Delete confirmation modal */}
      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)} centered>
        <Modal.Body style={{ padding: 28 }}>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <div style={{
              width: 48, height: 48, borderRadius: '50%',
              backgroundColor: '#ffe5e5',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 10px',
            }}>
              <BsTrash size={22} color="#8B0000" />
            </div>
            <h6 style={{ fontWeight: 700, marginBottom: 2 }}>Supprimer ce centre de prière ?</h6>
            <p style={{ fontSize: 13, color: '#666', margin: 0 }}>Cette action est irréversible.</p>
          </div>
          {deleteTarget && (
            <div style={{ backgroundColor: '#f7f8fa', borderRadius: 8, padding: '14px 16px', marginBottom: 24, fontSize: 13 }}>
              <div className="mb-1"><span className="fw-semibold">Nom : </span>{deleteTarget.nom}</div>
              <div className="mb-1"><span className="fw-semibold">Pays : </span>{deleteTarget.representation?.pays || countryName(deleteTarget.representation_id)}</div>
              <div><span className="fw-semibold">Type : </span>{typeLabel(deleteTarget.type_lieu)}</div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button
              onClick={() => setDeleteTarget(null)}
              style={{ padding: '8px 24px', borderRadius: 4, border: '1px solid #ccc', background: '#fff', cursor: 'pointer', fontSize: 13 }}
            >
              ANNULER
            </button>
            <button
              onClick={handleDelete}
              style={{ padding: '8px 24px', borderRadius: 4, border: 'none', backgroundColor: '#8B0000', color: '#fff', cursor: 'pointer', fontSize: 13 }}
            >
              SUPPRIMER
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}
